import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowRight, ArrowLeft, CheckCircle2, Sun, Thermometer, Phone, Mail, PartyPopper, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const beratungTranslations = {
  de: {
    title: 'Kostenlose Beratung',
    subtitle: 'Vereinbaren Sie einen kostenlosen Beratungstermin mit unseren Experten',
    step1Title: 'Was interessiert Sie?',
    step2Title: 'Details zu Ihrem Projekt',
    step3Title: 'Ihre Kontaktdaten',
    step4Title: 'Wunschtermin',
    photovoltaik: 'Photovoltaik',
    waermepumpe: 'Wärmepumpe',
    both: 'Beides',
    roofArea: 'Dachfläche (m²)',
    roofOrientation: 'Dachausrichtung',
    powerConsumption: 'Stromverbrauch (kWh/Jahr)',
    buildingType: 'Gebäudeart',
    livingArea: 'Wohnfläche (m²)',
    heatingType: 'Aktuelle Heizung',
    yearBuilt: 'Baujahr',
    persons: 'Personen im Haushalt',
    withStorage: 'Mit Batteriespeicher',
    withWallbox: 'Mit Wallbox',
    name: 'Name',
    email: 'E-Mail',
    phone: 'Telefon',
    address: 'Adresse',
    zip: 'PLZ',
    city: 'Ort',
    timeframe: 'Zeitrahmen',
    budget: 'Budget',
    preferredDate: 'Wunschtermin',
    preferredTime: 'Wunschzeit',
    next: 'Weiter',
    back: 'Zurück',
    submit: 'Anfrage absenden',
    sending: 'Wird gesendet...',
    successTitle: 'Vielen Dank!',
    successDesc: 'Wir haben Ihre Anfrage erhalten und melden uns in Kürze bei Ihnen.',
    backHome: 'Zurück zur Startseite',
    south: 'Süden',
    east: 'Osten',
    west: 'Westen',
    north: 'Norden',
    singleFamily: 'Einfamilienhaus',
    multiFamily: 'Mehrfamilienhaus',
    commercial: 'Gewerbe',
    gas: 'Gas',
    oil: 'Öl',
    electric: 'Strom',
    other: 'Sonstige',
    asap: 'So schnell wie möglich',
    month3: 'In 3 Monaten',
    month6: 'In 6 Monaten',
    year1: 'In 1 Jahr',
    morning: 'Vormittags',
    afternoon: 'Nachmittags',
    evening: 'Abends',
    heroTitle: 'Kostenlose Beratung & Planung',
    heroSubtitle: 'In nur 3 Schritten zu Ihrem individuellen Angebot',
    stepChoose: 'Projekt wählen',
    stepDetails: 'Details angeben',
    stepContact: 'Kontaktdaten',
    chooseYour: 'Wählen Sie Ihr ',
    project: 'Projekt',
    whatPlan: 'Was möchten Sie planen?',
    pvSystem: 'Photovoltaikanlage',
    pvSystemDesc: 'Solaranlage für Ihren eigenen Strom',
    pvB1: 'Bis zu 70% Kostenersparnis',
    pvB2: 'Unabhängigkeit',
    pvB3: 'CO2-neutral',
    wpDesc: 'Nachhaltig heizen und kühlen',
    wpB1: 'Bis zu 70% Förderung',
    wpB2: 'Niedrige Kosten',
    wpB3: 'Heizen & Kühlen',
    bothDesc: 'PV + Wärmepumpe Komplettlösung',
    bothB1: 'Maximale Ersparnis',
    bothB2: '100% Energiewende',
    bothB3: 'Beste Förderung',
    planPrefix: 'Ihre ',
    planSuffix: ' planen',
    pvAndWp: 'PV-Anlage & Wärmepumpe',
    pvOnly: 'PV-Anlage',
    wpGradient: 'Wärmepumpe',
    roofDetails: 'Geben Sie die technischen Details Ihres Daches an',
    buildingDetails: 'Geben Sie die Details Ihres Gebäudes an',
    pleaseSelect: 'Bitte wählen',
    dirSouth: 'Süd',
    dirSouthwest: 'Südwest',
    dirSoutheast: 'Südost',
    dirWest: 'West',
    dirEast: 'Ost',
    annualConsumption: 'Jährlicher Stromverbrauch (kWh)',
    bt3Commercial: 'Gewerbegebäude',
    additionalOptions: 'Zusätzliche Optionen',
    batteryWanted: 'Batteriespeicher gewünscht',
    wallboxWanted: 'Wallbox für E-Auto gewünscht',
    currentHeating: 'Aktuelle Heizungsart',
    htGas: 'Gasheizung',
    htOil: 'Ölheizung',
    htElectric: 'Stromheizung',
    htNone: 'Keine / Neubau',
    buildingYear: 'Baujahr des Gebäudes',
    personsCount: 'Anzahl Personen im Haushalt',
    contactReason: 'Damit wir Ihnen ein individuelles Angebot erstellen können',
    yourNamePh: 'Ihr Name',
    yourEmailPh: 'ihre@email.de',
    streetPh: 'Straße und Hausnummer',
    cityPh: 'Stadt',
    desiredTimeframe: 'Gewünschter Zeitrahmen',
    budgetOptional: 'Budget (optional)',
    budgetPh: 'z.B. 15.000 - 20.000 €',
    appointmentTitle: 'Wunschtermin für Beratung',
    dateLabel: 'Datum',
    timeLabel: 'Uhrzeit',
    thankYouName: 'Vielen Dank',
    requestSent: 'Ihre Anfrage wurde erfolgreich gesendet!',
    successDescFull: 'Wir haben Ihre Anfrage erhalten und werden uns schnellstmöglich bei Ihnen melden – in der Regel innerhalb von 24 Stunden.',
    nextSteps: 'So geht es weiter:',
    ns1: 'Wir prüfen Ihre Angaben und erstellen ein individuelles Angebot',
    ns2: 'Ein Experte wird sich telefonisch bei Ihnen melden',
    ns3: 'Gemeinsam planen wir Ihre Energielösung',
    urgentContact: 'Bei dringenden Fragen erreichen Sie uns:',
    errorTitle: 'Fehler',
    errorSendDesc: 'Es gab ein Problem beim Senden. Bitte versuchen Sie es erneut.',
    eg: 'z.B.'
  },
  en: {
    title: 'Free Consultation',
    subtitle: 'Schedule a free consultation with our experts',
    step1Title: 'What are you interested in?',
    step2Title: 'Details about your project',
    step3Title: 'Your contact information',
    step4Title: 'Preferred appointment',
    photovoltaik: 'Photovoltaics',
    waermepumpe: 'Heat Pump',
    both: 'Both',
    roofArea: 'Roof Area (m²)',
    roofOrientation: 'Roof Orientation',
    powerConsumption: 'Power Consumption (kWh/Year)',
    buildingType: 'Building Type',
    livingArea: 'Living Area (m²)',
    heatingType: 'Current Heating',
    yearBuilt: 'Year Built',
    persons: 'Persons in Household',
    withStorage: 'With Battery Storage',
    withWallbox: 'With Wallbox',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    zip: 'Zip Code',
    city: 'City',
    timeframe: 'Timeframe',
    budget: 'Budget',
    preferredDate: 'Preferred Date',
    preferredTime: 'Preferred Time',
    next: 'Next',
    back: 'Back',
    submit: 'Submit Request',
    sending: 'Sending...',
    successTitle: 'Thank You!',
    successDesc: 'We have received your request and will contact you shortly.',
    backHome: 'Back to Homepage',
    south: 'South',
    east: 'East',
    west: 'West',
    north: 'North',
    singleFamily: 'Single Family House',
    multiFamily: 'Multi-Family House',
    commercial: 'Commercial',
    gas: 'Gas',
    oil: 'Oil',
    electric: 'Electric',
    other: 'Other',
    asap: 'As soon as possible',
    month3: 'In 3 months',
    month6: 'In 6 months',
    year1: 'In 1 year',
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening',
    heroTitle: 'Free Consultation & Planning',
    heroSubtitle: 'In just 3 steps to your personalized offer',
    stepChoose: 'Choose Project',
    stepDetails: 'Enter Details',
    stepContact: 'Contact Details',
    chooseYour: 'Choose Your ',
    project: 'Project',
    whatPlan: 'What would you like to plan?',
    pvSystem: 'Photovoltaic System',
    pvSystemDesc: 'Solar system for your own electricity',
    pvB1: 'Up to 70% cost savings',
    pvB2: 'Independence',
    pvB3: 'CO2-neutral',
    wpDesc: 'Sustainable heating and cooling',
    wpB1: 'Up to 70% subsidy',
    wpB2: 'Low costs',
    wpB3: 'Heating & Cooling',
    bothDesc: 'PV + Heat Pump complete solution',
    bothB1: 'Maximum savings',
    bothB2: '100% Energy transition',
    bothB3: 'Best subsidy',
    planPrefix: 'Plan Your ',
    planSuffix: '',
    pvAndWp: 'PV System & Heat Pump',
    pvOnly: 'PV System',
    wpGradient: 'Heat Pump',
    roofDetails: 'Enter the technical details of your roof',
    buildingDetails: 'Enter the details of your building',
    pleaseSelect: 'Please select',
    dirSouth: 'South',
    dirSouthwest: 'Southwest',
    dirSoutheast: 'Southeast',
    dirWest: 'West',
    dirEast: 'East',
    annualConsumption: 'Annual Power Consumption (kWh)',
    bt3Commercial: 'Commercial Building',
    additionalOptions: 'Additional Options',
    batteryWanted: 'Battery storage desired',
    wallboxWanted: 'Wallbox for e-car desired',
    currentHeating: 'Current Heating Type',
    htGas: 'Gas Heating',
    htOil: 'Oil Heating',
    htElectric: 'Electric Heating',
    htNone: 'None / New Building',
    buildingYear: 'Year of Construction',
    personsCount: 'Number of People in Household',
    contactReason: 'So we can create a personalized offer for you',
    yourNamePh: 'Your Name',
    yourEmailPh: 'your@email.com',
    streetPh: 'Street and House Number',
    cityPh: 'City',
    desiredTimeframe: 'Desired Timeframe',
    budgetOptional: 'Budget (optional)',
    budgetPh: 'e.g. 15,000 - 20,000 €',
    appointmentTitle: 'Preferred Consultation Appointment',
    dateLabel: 'Date',
    timeLabel: 'Time',
    thankYouName: 'Thank You',
    requestSent: 'Your request has been sent successfully!',
    successDescFull: 'We have received your request and will get back to you as soon as possible – usually within 24 hours.',
    nextSteps: 'Next steps:',
    ns1: 'We review your details and create a personalized offer',
    ns2: 'An expert will contact you by phone',
    ns3: 'Together we plan your energy solution',
    urgentContact: 'For urgent questions, reach us:',
    errorTitle: 'Error',
    errorSendDesc: 'There was a problem sending. Please try again.',
    eg: 'e.g.'
  }
};

const BeratungPage = () => {
  const { toast } = useToast();
  const { language } = useLanguage();
  const t = beratungTranslations[language] || beratungTranslations.de;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [step, setStep] = useState(1);
  const [projektTyp, setProjektTyp] = useState('');
  
  // Store form data in refs to prevent re-renders
  const [formData, setFormData] = useState({
    dachflaeche: '',
    dachausrichtung: '',
    stromverbrauch: '',
    gebaeudeart: '',
    wohnflaeche: '',
    heizungsart: '',
    baujahr: '',
    personen: '',
    speicher: false,
    wallbox: false,
    name: '',
    email: '',
    telefon: '',
    adresse: '',
    plz: '',
    ort: '',
    zeitrahmen: '',
    budget: '',
    wunschtermin: '',
    wunschzeit: ''
  });

  // Simple update function that only updates the specific field
  const updateField = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const systemTypeMap = {
        'pv': 'photovoltaik',
        'wp': 'waermepumpe',
        'both': 'beide'
      };
      
      const response = await fetch(`${API_URL}/api/beratung`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          system_type: systemTypeMap[projektTyp] || 'photovoltaik',
          name: formData.name,
          email: formData.email,
          phone: formData.telefon,
          address: `${formData.adresse}, ${formData.ort}`,
          plz: formData.plz,
          roof_area: parseFloat(formData.dachflaeche) || null,
          annual_consumption: parseFloat(formData.stromverbrauch) || null,
          heating_type: formData.heizungsart || null,
          building_type: formData.gebaeudeart || null,
          message: `Zeitrahmen: ${formData.zeitrahmen || 'Nicht angegeben'}, Budget: ${formData.budget || 'Nicht angegeben'}, Speicher: ${formData.speicher ? 'Ja' : 'Nein'}, Wallbox: ${formData.wallbox ? 'Ja' : 'Nein'}`
        }),
      });
      
      if (response.ok) {
        setIsSuccess(true);
      } else {
        throw new Error('Fehler beim Senden');
      }
    } catch (error) {
      toast({
        title: t.errorTitle,
        description: t.errorSendDesc,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Success screen after form submission
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Header />
        
        <section className="pt-32 pb-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto text-center">
              <Card className="border-0 shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8">
                  <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <CheckCircle2 className="w-14 h-14 text-green-500" />
                  </div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                    {t.thankYouName}, {formData.name}!
                  </h1>
                </div>
                
                <CardContent className="p-8 sm:p-12">
                  <div className="flex items-center justify-center gap-2 mb-6">
                    <PartyPopper className="w-8 h-8 text-amber-500" />
                    <h2 className="text-2xl font-bold text-gray-900">
                      {t.requestSent}
                    </h2>
                    <PartyPopper className="w-8 h-8 text-amber-500" />
                  </div>
                  
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    {t.successDescFull}
                  </p>
                  
                  <div className="bg-sky-50 rounded-xl p-6 mb-8">
                    <h3 className="font-bold text-gray-900 mb-4">{t.nextSteps}</h3>
                    <div className="space-y-3 text-left">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-sky-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">1</div>
                        <p className="text-gray-700">{t.ns1}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-sky-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">2</div>
                        <p className="text-gray-700">{t.ns2}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-sky-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">3</div>
                        <p className="text-gray-700">{t.ns3}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <p className="text-gray-600 mb-4">{t.urgentContact}</p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <a href="tel:+4915782283963" className="flex items-center justify-center gap-2 text-sky-600 hover:text-sky-700 font-semibold">
                        <Phone className="w-5 h-5" />
                        +49 157 82283963
                      </a>
                      <a href="mailto:Buchhaltung@gravante.de" className="flex items-center justify-center gap-2 text-sky-600 hover:text-sky-700 font-semibold">
                        <Mail className="w-5 h-5" />
                        Buchhaltung@gravante.de
                      </a>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={() => window.location.href = '/'}
                    className="mt-8 bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 hover:to-sky-800 text-white px-8 py-3 text-lg"
                  >
                    {t.backHome}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-gradient-to-r from-sky-600 to-amber-500">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              {t.heroTitle}
            </h1>
            <p className="text-xl mb-2">
              {t.heroSubtitle}
            </p>
            
            {/* Progress Steps */}
            <div className="flex justify-center items-center mt-8 space-x-4">
              <div className={`flex items-center ${step >= 1 ? 'text-white' : 'text-white/50'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-white text-sky-600' : 'bg-white/30'} font-bold`}>
                  1
                </div>
                <span className="ml-2 hidden sm:inline">{t.stepChoose}</span>
              </div>
              <div className="w-12 h-0.5 bg-white/50"></div>
              <div className={`flex items-center ${step >= 2 ? 'text-white' : 'text-white/50'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-white text-sky-600' : 'bg-white/30'} font-bold`}>
                  2
                </div>
                <span className="ml-2 hidden sm:inline">{t.stepDetails}</span>
              </div>
              <div className="w-12 h-0.5 bg-white/50"></div>
              <div className={`flex items-center ${step >= 3 ? 'text-white' : 'text-white/50'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-white text-sky-600' : 'bg-white/30'} font-bold`}>
                  3
                </div>
                <span className="ml-2 hidden sm:inline">{t.stepContact}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Step 1: Projekt auswählen */}
          {step === 1 && (
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  {t.chooseYour}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{t.project}</span>
                </h2>
                <p className="text-lg text-gray-600">
                  {t.whatPlan}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <Card 
                  className={`cursor-pointer border-2 hover:shadow-xl transition-all duration-300 ${projektTyp === 'pv' ? 'border-sky-600 shadow-xl' : 'border-gray-200'}`}
                  onClick={() => setProjektTyp('pv')}
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-sky-100 to-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Sun className="w-10 h-10 text-sky-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{t.pvSystem}</h3>
                    <p className="text-gray-600 mb-4">
                      {t.pvSystemDesc}
                    </p>
                    <ul className="text-left space-y-2 text-sm text-gray-700">
                      <li className="flex items-center">
                        <CheckCircle2 className="w-4 h-4 text-sky-600 mr-2" />
                        {t.pvB1}
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-4 h-4 text-sky-600 mr-2" />
                        {t.pvB2}
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-4 h-4 text-sky-600 mr-2" />
                        {t.pvB3}
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card 
                  className={`cursor-pointer border-2 hover:shadow-xl transition-all duration-300 ${projektTyp === 'waermepumpe' ? 'border-amber-600 shadow-xl' : 'border-gray-200'}`}
                  onClick={() => setProjektTyp('waermepumpe')}
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Thermometer className="w-10 h-10 text-amber-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{t.waermepumpe}</h3>
                    <p className="text-gray-600 mb-4">
                      {t.wpDesc}
                    </p>
                    <ul className="text-left space-y-2 text-sm text-gray-700">
                      <li className="flex items-center">
                        <CheckCircle2 className="w-4 h-4 text-amber-600 mr-2" />
                        {t.wpB1}
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-4 h-4 text-amber-600 mr-2" />
                        {t.wpB2}
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-4 h-4 text-amber-600 mr-2" />
                        {t.wpB3}
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card 
                  className={`cursor-pointer border-2 hover:shadow-xl transition-all duration-300 ${projektTyp === 'beide' ? 'border-green-600 shadow-xl' : 'border-gray-200'}`}
                  onClick={() => setProjektTyp('beide')}
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <div className="flex">
                        <Sun className="w-8 h-8 text-green-600" />
                        <Thermometer className="w-8 h-8 text-blue-600 -ml-2" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{t.both}</h3>
                    <p className="text-gray-600 mb-4">
                      {t.bothDesc}
                    </p>
                    <ul className="text-left space-y-2 text-sm text-gray-700">
                      <li className="flex items-center">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                        {t.bothB1}
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                        {t.bothB2}
                      </li>
                      <li className="flex items-center">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mr-2" />
                        {t.bothB3}
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {projektTyp && (
                <div className="text-center mt-8">
                  <Button 
                    onClick={() => setStep(2)}
                    size="lg"
                    className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-6"
                  >
                    {t.next}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Technische Daten PV */}
          {step === 2 && (projektTyp === 'pv' || projektTyp === 'beide') && (
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {t.planPrefix}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">
                    {projektTyp === 'beide' ? t.pvAndWp : t.pvOnly}
                  </span>{t.planSuffix}
                </h2>
                <p className="text-gray-600">
                  {t.roofDetails}
                </p>
              </div>

              <Card className="border-2 border-sky-100 mb-8">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{t.pvSystem}</h3>
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.roofArea} *
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          defaultValue={formData.dachflaeche}
                          onBlur={(e) => updateField('dachflaeche', e.target.value)}
                          placeholder={`${t.eg} 50`}
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.roofOrientation} *
                        </label>
                        <select
                          defaultValue={formData.dachausrichtung}
                          onChange={(e) => updateField('dachausrichtung', e.target.value)}
                          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-600 bg-white"
                        >
                          <option value="">{t.pleaseSelect}</option>
                          <option value="sued">{t.dirSouth}</option>
                          <option value="suedwest">{t.dirSouthwest}</option>
                          <option value="suedost">{t.dirSoutheast}</option>
                          <option value="west">{t.dirWest}</option>
                          <option value="ost">{t.dirEast}</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.annualConsumption} *
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          defaultValue={formData.stromverbrauch}
                          onBlur={(e) => updateField('stromverbrauch', e.target.value)}
                          placeholder={`${t.eg} 4000`}
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.buildingType} *
                        </label>
                        <select
                          defaultValue={formData.gebaeudeart}
                          onChange={(e) => updateField('gebaeudeart', e.target.value)}
                          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-600 bg-white"
                        >
                          <option value="">{t.pleaseSelect}</option>
                          <option value="einfamilienhaus">{t.singleFamily}</option>
                          <option value="mehrfamilienhaus">{t.multiFamily}</option>
                          <option value="gewerbe">{t.bt3Commercial}</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        {t.additionalOptions}
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            defaultChecked={formData.speicher}
                            onChange={(e) => updateField('speicher', e.target.checked)}
                            className="w-5 h-5 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                          />
                          <span className="ml-3 text-gray-700">{t.batteryWanted}</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            defaultChecked={formData.wallbox}
                            onChange={(e) => updateField('wallbox', e.target.checked)}
                            className="w-5 h-5 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
                          />
                          <span className="ml-3 text-gray-700">{t.wallboxWanted}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {projektTyp === 'beide' && (
                <Card className="border-2 border-amber-100 mb-8">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{t.waermepumpe}</h3>
                    <div className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t.livingArea} *
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            defaultValue={formData.wohnflaeche}
                            onBlur={(e) => updateField('wohnflaeche', e.target.value)}
                            placeholder={`${t.eg} 150`}
                            className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-amber-600"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t.currentHeating} *
                          </label>
                          <select
                            defaultValue={formData.heizungsart}
                            onChange={(e) => updateField('heizungsart', e.target.value)}
                            className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-600 bg-white"
                          >
                            <option value="">{t.pleaseSelect}</option>
                            <option value="gas">{t.htGas}</option>
                            <option value="oel">{t.htOil}</option>
                            <option value="strom">{t.htElectric}</option>
                            <option value="keine">{t.htNone}</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t.buildingYear} *
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            defaultValue={formData.baujahr}
                            onBlur={(e) => updateField('baujahr', e.target.value)}
                            placeholder={`${t.eg} 1990`}
                            className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-amber-600"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t.personsCount} *
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            defaultValue={formData.personen}
                            onBlur={(e) => updateField('personen', e.target.value)}
                            placeholder={`${t.eg} 4`}
                            className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-amber-600"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex justify-between mt-8">
                <Button 
                  onClick={() => setStep(1)}
                  variant="outline"
                  size="lg"
                >
                  <ArrowLeft className="mr-2 w-5 h-5" />
                  {t.back}
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  size="lg"
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                >
                  {t.next}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Technische Daten Wärmepumpe */}
          {step === 2 && projektTyp === 'waermepumpe' && (
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {t.planPrefix}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{t.wpGradient}</span>{t.planSuffix}
                </h2>
                <p className="text-gray-600">{t.buildingDetails}</p>
              </div>

              <Card className="border-2 border-amber-100">
                <CardContent className="p-8">
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.livingArea} *
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          defaultValue={formData.wohnflaeche}
                          onBlur={(e) => updateField('wohnflaeche', e.target.value)}
                          placeholder={`${t.eg} 150`}
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-amber-600"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.currentHeating} *
                        </label>
                        <select
                          defaultValue={formData.heizungsart}
                          onChange={(e) => updateField('heizungsart', e.target.value)}
                          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-600 bg-white"
                        >
                          <option value="">{t.pleaseSelect}</option>
                          <option value="gas">{t.htGas}</option>
                          <option value="oel">{t.htOil}</option>
                          <option value="strom">{t.htElectric}</option>
                          <option value="keine">{t.htNone}</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.buildingYear} *
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          defaultValue={formData.baujahr}
                          onBlur={(e) => updateField('baujahr', e.target.value)}
                          placeholder={`${t.eg} 1990`}
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-amber-600"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.personsCount} *
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          defaultValue={formData.personen}
                          onBlur={(e) => updateField('personen', e.target.value)}
                          placeholder={`${t.eg} 4`}
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-600 focus:border-amber-600"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-between mt-8">
                <Button 
                  onClick={() => setStep(1)}
                  variant="outline"
                  size="lg"
                >
                  <ArrowLeft className="mr-2 w-5 h-5" />
                  {t.back}
                </Button>
                <Button 
                  onClick={() => setStep(3)}
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {t.next}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Persönliche Daten */}
          {step === 3 && (
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  {t.planPrefix}<span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{t.stepContact}</span>{t.planSuffix}
                </h2>
                <p className="text-gray-600">{t.contactReason}</p>
              </div>

              <Card className="border-2 border-sky-100">
                <CardContent className="p-8">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.name} *
                      </label>
                      <input
                        type="text"
                        defaultValue={formData.name}
                        onBlur={(e) => updateField('name', e.target.value)}
                        placeholder={t.yourNamePh}
                        className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.email} *
                        </label>
                        <input
                          type="email"
                          defaultValue={formData.email}
                          onBlur={(e) => updateField('email', e.target.value)}
                          placeholder={t.yourEmailPh}
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.phone} *
                        </label>
                        <input
                          type="tel"
                          defaultValue={formData.telefon}
                          onBlur={(e) => updateField('telefon', e.target.value)}
                          placeholder="+49 123 456 789"
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.address} *
                      </label>
                      <input
                        type="text"
                        defaultValue={formData.adresse}
                        onBlur={(e) => updateField('adresse', e.target.value)}
                        placeholder={t.streetPh}
                        className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.zip} *
                        </label>
                        <input
                          type="text"
                          defaultValue={formData.plz}
                          onBlur={(e) => updateField('plz', e.target.value)}
                          placeholder="12345"
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.city} *
                        </label>
                        <input
                          type="text"
                          defaultValue={formData.ort}
                          onBlur={(e) => updateField('ort', e.target.value)}
                          placeholder={t.cityPh}
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.desiredTimeframe}
                        </label>
                        <select
                          defaultValue={formData.zeitrahmen}
                          onChange={(e) => updateField('zeitrahmen', e.target.value)}
                          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-600 bg-white"
                        >
                          <option value="">{t.pleaseSelect}</option>
                          <option value="sofort">{t.asap}</option>
                          <option value="3monate">{t.month3}</option>
                          <option value="6monate">{t.month6}</option>
                          <option value="1jahr">{t.year1}</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t.budgetOptional}
                        </label>
                        <input
                          type="text"
                          defaultValue={formData.budget}
                          onBlur={(e) => updateField('budget', e.target.value)}
                          placeholder={t.budgetPh}
                          className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
                        />
                      </div>
                    </div>

                    {/* Appointment Booking */}
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Calendar className="w-5 h-5 mr-2 text-sky-600" />
                        {t.appointmentTitle}
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t.dateLabel}
                          </label>
                          <input
                            type="date"
                            defaultValue={formData.wunschtermin}
                            onChange={(e) => updateField('wunschtermin', e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-sky-600"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t.timeLabel}
                          </label>
                          <select
                            defaultValue={formData.wunschzeit}
                            onChange={(e) => updateField('wunschzeit', e.target.value)}
                            className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-600 bg-white"
                          >
                            <option value="">{t.pleaseSelect}</option>
                            <option value="09:00">09:00 - 10:00</option>
                            <option value="10:00">10:00 - 11:00</option>
                            <option value="11:00">11:00 - 12:00</option>
                            <option value="13:00">13:00 - 14:00</option>
                            <option value="14:00">14:00 - 15:00</option>
                            <option value="15:00">15:00 - 16:00</option>
                            <option value="16:00">16:00 - 17:00</option>
                            <option value="17:00">17:00 - 18:00</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-between mt-8">
                <Button 
                  onClick={() => setStep(2)}
                  variant="outline"
                  size="lg"
                >
                  <ArrowLeft className="mr-2 w-5 h-5" />
                  {t.back}
                </Button>
                <Button 
                  onClick={handleSubmit}
                  size="lg"
                  disabled={isSubmitting}
                  className="bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t.sending}
                    </>
                  ) : (
                    <>
                      {t.submit}
                      <CheckCircle2 className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
          
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BeratungPage;
